import { AuthenticationError, type GrantedAuthority, type CredentialsHolder } from '../types.ts';

export type UserDetails = {
    readonly username: string;
    readonly password?: string | null; // null means credentials have been erased
    readonly authorities: ReadonlyArray<GrantedAuthority>;

    readonly accountExpired?: boolean;
    // Indicates whether the user's account is locked. A locked account prevents authentication.
    readonly accountLocked?: boolean;
    // Indicates whether the user's credentials (password) has expired. Expired credentials prevent authentication.
    readonly credentialsExpired?: boolean;
    // Indicates whether the user is enabled or disabled. A disabled user cannot be authenticated.
    readonly disabled?: boolean;
};

export type UserDetailsChecker = (user: UserDetails) => void;

export interface UserDetailsService {
    findByUsername(username: string): Promise<UserDetails | undefined>;
}

export interface UserDetailsPasswordService {
    updatePassword(user: UserDetails, newPassword?: string): Promise<UserDetails>;
}

export const NoopUserDetailsPasswordService: UserDetailsPasswordService = {
    async updatePassword(user: UserDetails, _newPassword?: string): Promise<UserDetails> {
        return user;
    }
}

export class UsernameNotFoundError extends AuthenticationError {
    readonly username?: string;

    constructor(message: string, username?: string, options?: ErrorOptions) {
        super(message, options);
        this.username = username;
    }
}

export type User = UserDetails & CredentialsHolder & { toString(): string };

export class UserBuilder {
    #username!: string;
    #password?: string | null;
    #authorities: ReadonlyArray<GrantedAuthority> = [];
    #accountExpired?: boolean;
    #accountLocked?: boolean;
    #credentialsExpired?: boolean;
    #disabled?: boolean;

    #passwordEncoder: (rawPassword?: string | null) => string | undefined | null = (rawPassword) => rawPassword;

    private constructor() {
    }

    static ofUsername(username: string): UserBuilder {
        return new UserBuilder().username(username);
    }

    static ofUserDetails(user: UserDetails): UserBuilder {
        const builder = UserBuilder.ofUsername(user.username)
            .accountExpired(user.accountExpired ?? false)
            .accountLocked(user.accountLocked ?? false)
            .authorities(user.authorities)
            .credentialsExpired(user.credentialsExpired ?? false)
            .disabled(user.disabled ?? false);
        if (user.password !== undefined) {
            builder.password(user.password);
        }
        return builder;
    }

    username(username: string): this {
        if (!username) {
            throw new TypeError('username cannot be empty');
        }
        this.#username = username;
        return this;
    }

    password(password?: string | null): this {
        this.#password = password;
        return this;
    }

    passwordEncoder(encoder: (rawPassword?: string | null) => string | null | undefined): this {
        if (!encoder) {
            throw new TypeError('password encoder cannot be null or undefined');
        }
        this.#passwordEncoder = encoder;
        return this;
    }

    roles(...roles: string[]): this {
        return this.authorities(roles.map(role => {
            if (role.startsWith('role:')) {
                throw new Error(`${role}  must not start with 'role:' (it is automatically added)`);
            }
            const authority = `role:${role}`;
            return { authority };
        }));
    }

    authorities(authorities: ReadonlyArray<GrantedAuthority>): this {
        this.#authorities = [...authorities];
        return this;
    }

    accountExpired(accountExpired: boolean): this {
        this.#accountExpired = accountExpired;
        return this;
    }

    accountLocked(accountLocked: boolean): this {
        this.#accountLocked = accountLocked;
        return this;
    }

    credentialsExpired(credentialsExpired: boolean): this {
        this.#credentialsExpired = credentialsExpired;
        return this;
    }

    disabled(disabled: boolean): this {
        this.#disabled = disabled;
        return this;
    }

    build(): User {
        if (!this.#username) {
            throw new TypeError('username is required');
        }
        let encodedPassword: string | null | undefined = this.#password !== undefined ? this.#passwordEncoder(this.#password) : undefined;
        return {
            username: this.#username,
            password: encodedPassword,
            authorities: this.#authorities,
            accountExpired: this.#accountExpired,
            accountLocked: this.#accountLocked,
            credentialsExpired: this.#credentialsExpired,
            disabled: this.#disabled,
            eraseCredentials(): void {
                encodedPassword = null;
            },
            toString(): string {
                return `User(username=${this.username}, password=[PROTECTED], authorities=${JSON.stringify(this.authorities)}, accountExpired=${this.accountExpired}, accountLocked=${this.accountLocked}, credentialsExpired=${this.credentialsExpired}, disabled=${this.disabled})`;
            }
        }
    }
}
